import { IconApps, IconBranch, IconCloud, IconCommand, IconThunderbolt, IconWifi } from '@arco-design/web-react/icon';

import { IconDataSource } from '@/components/Icon/IconDataSource';
import { IconLargeScreen } from '@/components/Icon/IconLargeScreen';
import { IconLogAccess } from '@/components/Icon/IconLogAccess';
import { IconLogRules } from '@/components/Icon/IconLogRules';
import { IconMonitorRules } from '@/components/Icon/IconMonitorRules';
import { SUB_CENTER } from '@/constants/subCenter';
import { BARE_METAL_POOL_ICON } from '@/pages/data-center/assets/bare-metal-pool/constants';
import { BARE_METAL_NODE_ICON } from '@/pages/data-center/assets/computing/constants';
import { GROUPS } from '@/service/apiConf';

import { getBizCenterMenuChildren } from './bizCenterMenu';

export const getDataCenterMenu = (cluster) => {
  cluster = cluster || '-';
  const dc = `/data-center/${cluster}`;
  const resource = `${dc}/${SUB_CENTER.RESOURCE}`;
  const ai = `${dc}/${SUB_CENTER.AI}`;
  const app = `${dc}/${SUB_CENTER.APP}`;
  const ops = `${dc}/${SUB_CENTER.OPS}`;
  const biz = `${dc}/${SUB_CENTER.BIZ}`;
  return [
    {
      // 平台管理
      title: 'dataCenterMode',
      icon: 'icon-Data',
      children: [
        // 算力中心（默认主视图，无独立 URL 前缀）
        {
          title: 'computeCenter',
          icon: 'icon-suanli',
          externalLink: true,
          fixBottom: true,
          centerKey: SUB_CENTER.COMPUTE,
          children: [
            {
              title: 'computeCenterOverview',
              icon: 'icon-gailan',
              path: `${dc}/overview`,
              isLeaf: true,
              permissionVisibleWithParent: true,
              viewAuth: [GROUPS.datacenter, 'compute-center/overview', 'get'],
            },
            // 制品中心
            {
              title: 'productsCatalogue',
              icon: 'icon-chanpin',
              children: [
                {
                  title: 'systemImagesNew',
                  icon: 'icon-jingxiang',
                  path: `/data-center/${cluster}/system-images`,
                  viewAuth: [GROUPS.image, 'system-images', 'list'],
                  // 制品中心-系统镜像仓库
                },
                {
                  title: 'drivers',
                  icon: 'icon-plug',
                  path: `/data-center/${cluster}/drivers`,
                  viewAuth: [GROUPS.image, 'drivers', 'list'],
                },
                {
                  title: 'runtime',
                  icon: 'icon-runtime',
                  path: `/data-center/${cluster}/runtime`,
                  viewAuth: [GROUPS.image, 'runtime', 'list'],
                },
                {
                  title: 'tool',
                  icon: 'icon-tool',
                  path: `/data-center/${cluster}/tool`,
                  viewAuth: [GROUPS.image, 'tool', 'list'],
                },
              ],
            },
            {
              title: 'instances',
              icon: 'icon-Vastack',
              children: [
                // 容器服务
                {
                  title: 'containerInstances',
                  path: `/data-center/${cluster}/container/instance`,
                  icon: 'icon-a-rongqi2',
                  viewAuth: [GROUPS.computing, 'containerinstances', 'list'],
                },
                // 虚拟机服务
                {
                  title: 'VMInstances',
                  path: `/data-center/${cluster}/cloud/instance`,
                  icon: 'icon-yunzhuji',
                  viewAuth: [GROUPS.computing, 'virtualmachines', 'list'],
                },
                {
                  title: 'elasticBareMetalInstance',
                  path: `/data-center/${cluster}/ebm/instance`,
                  icon: 'icon-luojinshu1',
                  viewAuth: [GROUPS.computing, 'elasticbaremetals', 'list'],
                },
                // 裸金属服务
                {
                  title: 'bareMetalInstances',
                  path: `/data-center/${cluster}/bare-metal/instance`,
                  icon: 'icon-Vastack',
                  viewAuth: [GROUPS.computing, 'instances', 'list'],
                },
              ],
            },
            {
              title: 'networkSecurity',
              icon: 'icon-wangluo',
              children: [
                {
                  title: 'vpc',
                  path: `/data-center/${cluster}/vpc`,
                  icon: 'icon-VPC',
                  viewAuth: [GROUPS.network, 'vpcs', 'list'],
                },
                // 安全组
                {
                  title: 'securityGroup',
                  path: `/data-center/${cluster}/safegroup`,
                  icon: 'icon-a-baomizidianmian',
                  viewAuth: [GROUPS.network, 'securitygroups', 'list'],
                },
                {
                  title: 'eip',
                  path: `/data-center/${cluster}/eip`,
                  icon: 'icon-a-danxinggongwangIPEIP',
                  viewAuth: [GROUPS.network, 'eips', 'list'],
                },
              ],
            },
            // 容器存储
            {
              title: 'intelligentStorage',
              icon: 'icon-cunchu',
              children: [
                {
                  title: 'cloudHostCloudDisk',
                  path: `/data-center/${cluster}/vm/cloud-disk`,
                  icon: 'icon-yunzhuji',
                  viewAuth: ['storage.stack.io', 'volumes', 'list'], // 平台-云主机-权限
                },
                {
                  title: 'persistentVolumes',
                  path: `/data-center/${cluster}/pvcs`,
                  viewAuth: [GROUPS.persistentvolumeclaim, 'persistentvolumeclaims', 'list'],
                  icon: 'icon-juankuaizhaolei',
                },
              ],
            },
          ],
        },
        // 模型中心（外链打开子中心）
        {
          title: 'aiCenter',
          icon: 'icon-model',
          externalLink: true,
          fixBottom: true,
          centerKey: SUB_CENTER.AI,
          children: [
            {
              title: 'aiOverview',
              icon: 'icon-gailan',
              path: `${ai}/overview`,
              viewAuth: [GROUPS.llmgw, 'llmgwoverview', 'list'],
            },
            {
              title: 'modelExperience',
              icon: 'icon-kongzhitai',
              children: [
                {
                  title: 'textModelExperience',
                  icon: 'icon-zhongduan',
                  path: `${ai}/model-center/model-experience/text`,
                  viewAuth: [GROUPS.llmgw, 'v1/models', 'get'],
                },
                {
                  title: 'audioModelExperience',
                  icon: 'icon-tongzhishezhi',
                  path: `${ai}/model-center/model-experience/audio`,
                  viewAuth: [GROUPS.llmgw, 'v1/models', 'get'],
                },
                {
                  title: 'visionModelExperience',
                  icon: 'icon-VideoCamera',
                  path: `${ai}/model-center/model-experience/vision`,
                  viewAuth: [GROUPS.llmgw, 'v1/models', 'get'],
                },
                {
                  title: 'embeddingModelExperience',
                  icon: 'icon-Data',
                  path: `${ai}/model-center/model-experience/embedding`,
                  viewAuth: [GROUPS.llmgw, 'v1/models', 'get'],
                },
              ],
            },
            {
              title: 'modelService',
              icon: 'icon-moxingshili',
              children: [
                {
                  title: 'inferenceFramework',
                  icon: 'icon-infer',
                  path: `${ai}/model-center/inference-framework`,
                  viewAuth: [GROUPS.image, 'inference-framework', 'list'],
                },

                {
                  title: 'modelPlaza',
                  icon: 'icon-model',
                  path: `${ai}/model-center/model-plaza`,
                  isLeaf: true,
                  viewAuth: [GROUPS.ophub, 'artifacts', 'list'],
                },
                {
                  title: 'modelInstance',
                  path: `${ai}/model-center/model-instance`,
                  icon: 'icon-moxingshili',
                  isLeaf: true,
                  viewAuth: [GROUPS.inferenceMaas, 'modelinstances', 'list'],
                },
                {
                  title: 'modelSubscription',
                  path: `${ai}/model-center/model-subscription`,
                  icon: 'icon-dingyue',
                  viewAuth: [GROUPS.llmgw, 'llmgwmodelbindings', 'list'],
                },
                {
                  title: 'modelProviderManagement',
                  icon: 'icon-changshang',
                  path: `${ai}/model-center/model-provider`,
                  viewAuth: [GROUPS.llmgw, 'llmgwstrategies', 'list'],
                  // 厂商目录 /providers 使用独立 Casbin 资源，需与策略列表权限一并校验
                  viewAuthExtra: [[GROUPS.llmgw, 'providers', 'list']],
                },
              ],
            },
            {
              title: 'modelTraining',
              icon: 'icon-Data',
              children: [
                {
                  title: 'trainingTask',
                  icon: 'icon-rizhi',
                  path: `${ai}/model-center/training-task`,
                  isLeaf: true,
                  viewAuth: [GROUPS.trainingMaas, 'trainingtasks', 'list'],
                },
                {
                  title: 'trainingCluster',
                  icon: 'icon-jiqun',
                  path: `${ai}/model-center/training-cluster`,
                  isLeaf: true,
                  viewAuth: [GROUPS.trainingMaas, 'trainingclusters', 'list'],
                },
              ],
            },
            {
              title: 'dataAssets',
              icon: 'icon-model',
              children: [
                {
                  title: 'modelRepos',
                  icon: 'icon-model',
                  path: `${ai}/model-center/model-repos`,
                  viewAuth: [GROUPS.image, 'model-repos', 'list'],
                },
                {
                  title: 'datasetRepos',
                  icon: 'icon-shujuji',
                  path: `${ai}/model-center/dataset-repos`,
                  viewAuth: [GROUPS.image, 'dataset-repos', 'list'],
                },
              ],
            },
            {
              title: 'securityGovernance',
              icon: 'icon-zhidu',
              children: [
                {
                  title: 'modelDesensitization',
                  icon: 'icon-zhidu',
                  path: `${ai}/model-center/model-desensitization`,
                  viewAuth: [GROUPS.llmgw, 'llmgwdesensitizationpolicies', 'list'],
                },
                {
                  title: 'modelAuditLog',
                  icon: 'icon-rizhi',
                  path: `${ai}/model-center/audit-log`,
                  viewAuth: [GROUPS.llmgw, 'llmgwaudits', 'list'],
                },
              ],
            },
          ],
        },
        // 应用中心（外链打开子中心）
        {
          title: 'appCenter',
          icon: 'icon-ai-app',
          externalLink: true,
          fixBottom: true,
          centerKey: SUB_CENTER.APP,
          children: [
            {
              title: 'applicationMarket',
              children: [
                {
                  title: 'skillPlaza',
                  icon: 'icon-skills',
                  path: `${app}/skill-plaza`,
                  isLeaf: true,
                  viewAuth: [GROUPS.ophub, 'artifacts', 'list'],
                },
                {
                  title: 'appPlaza',
                  icon: 'icon-ai-app',
                  path: `${app}/app-plaza`,
                  isLeaf: true,
                  viewAuth: [GROUPS.ophub, 'artifacts', 'list'],
                },
              ],
            },
            {
              title: 'instanceManagement',
              children: [
                {
                  title: 'appInstance',
                  icon: 'icon-chanpinguanli',
                  path: `${app}/app-instance`,
                  isLeaf: true,
                  viewAuth: [GROUPS.maas, 'applicationinstances', 'list'],
                },
              ],
            },
            {
              title: 'assetManagement',
              children: [
                {
                  title: 'skills',
                  icon: 'icon-skills',
                  path: `${app}/skills`,
                  isLeaf: true,
                  viewAuth: [GROUPS.image, 'skills', 'list'],
                },
                {
                  title: 'aiApplications',
                  path: `${app}/ai-applications`,
                  isLeaf: true,
                  icon: 'icon-ai-app',
                  viewAuth: [GROUPS.image, 'ai-applications', 'list'],
                },
              ],
            },
          ],
        },
        // 运维中心（外链打开子中心）
        {
          title: 'operations',
          icon: 'icon-yunweizhongxin3',
          externalLink: true,
          fixBottom: true,
          centerKey: SUB_CENTER.OPS,
          children: [
            {
              title: 'opsOverview',
              path: `${ops}/overview`,
              icon: 'icon-gailan',
              viewAuth: [GROUPS.monitor, 'alertoverview', 'list'],
            },
            {
              title: 'situationAwareness',
              icon: 'icon-taishiganzhi',
              children: [
                {
                  title: 'clusterObservability',
                  icon: 'icon-jiqunzhuangtai',
                  path: `${ops}/situation-awareness/cluster`,
                  viewAuth: [GROUPS.perception, 'cluster', 'get'],
                },
                {
                  title: 'containerObservability',
                  icon: 'icon-rongqilujing',
                  path: `${ops}/situation-awareness/container`,
                  viewAuth: [GROUPS.perception, 'container', 'get'],
                },
                {
                  title: 'gpuObservability',
                  icon: 'icon-GPU',
                  path: `${ops}/situation-awareness/gpu`,
                  viewAuth: [GROUPS.perception, 'gpu', 'get'],
                },
                {
                  title: 'energyAnalyticsaim',
                  path: `${ops}/report/elec-report`,
                  icon: 'icon-Outline',
                  viewAuth: [GROUPS.perception, 'datareport', 'get'],
                },
                {
                  title: 'situationAwarenessDatasources',
                  path: `${ops}/situation-awareness/datasources`,
                  icon: 'icon-Setting',
                  viewAuth: [GROUPS.perception, 'datasources', 'list'],
                },
              ],
            },
            {
              title: 'monitoring',
              icon: 'icon-yunweizhongxin3',
              children: [
                {
                  title: 'visualization',
                  path: `${ops}/monitor-center/largescreen-config`,
                  icon: IconLargeScreen,
                  viewAuth: [GROUPS.monitor, 'dashboards', 'list'],
                },
                {
                  title: 'metricsExplorer',
                  path: `${ops}/monitor-center/metric-query`,
                  icon: 'icon-zhibiao',
                  viewAuth: [GROUPS.monitor, 'explore', 'list'],
                },
                {
                  title: 'aiAlert',
                  path: `${ops}/monitor-center/rule-configuration`,
                  icon: IconMonitorRules,
                  viewAuth: [GROUPS.monitor, 'monitorrules', 'list'],
                },
                {
                  title: 'monitorAlertLogs',
                  path: `${ops}/alert-center/alert-monitor`,
                  icon: 'icon-gaojingjilu',
                  viewAuth: [GROUPS.monitor, 'alertrecords', 'list'],
                },
                {
                  title: 'monitoringIntegration',
                  path: `${ops}/monitor-center/data-access`,
                  icon: IconDataSource,
                  viewAuth: [GROUPS.monitor, 'datasources', 'list'],
                },
              ],
            },
            {
              title: 'logCenter',
              icon: 'icon-rizhizhongxin',
              children: [
                {
                  title: 'logIngestion',
                  path: `${ops}/log-center/log-access`,
                  icon: IconLogAccess,
                  viewAuth: [GROUPS.log, 'tables', 'list'],
                },
                {
                  title: 'logAIAlert',
                  path: `${ops}/log-center/log-rule`,
                  icon: IconLogRules,
                  viewAuth: [GROUPS.log, 'logrules', 'list'],
                },
                {
                  title: 'alertLogs',
                  path: `${ops}/alert-center/alert-log`,
                  icon: 'icon-gaojingjilu',
                  viewAuth: [GROUPS.log, 'logalertrecords', 'list'],
                },
              ],
            },
            {
              title: 'notificationSettings',
              icon: 'icon-tongzhishezhi',
              children: [
                {
                  title: 'notificationChannels',
                  path: `${ops}/alert-center/alert-medium`,
                  icon: 'icon-tongzhishezhi',
                  viewAuth: ['alertmanager.stack.io', 'alertmediums', 'list'],
                },
              ],
            },
          ],
        },
        // 运营中心（外链打开子中心，含原运营中心 + 费用中心）
        {
          title: 'operationsGovernance',
          icon: 'icon-yunyingguanli1',
          externalLink: true,
          fixBottom: true,
          centerKey: SUB_CENTER.BIZ,
          children: getBizCenterMenuChildren(biz),
        },
        // 资源中心（仅数据中心）
        {
          title: 'resourceCenter',
          icon: 'icon-jiqunjiedian',
          externalLink: true,
          fixBottom: true,
          centerKey: SUB_CENTER.RESOURCE,
          children: [
            {
              title: 'resourceCenterOverview',
              icon: 'icon-gailan',
              path: `${resource}/overview`,
              isLeaf: true,
              permissionVisibleWithParent: true,
              viewAuth: [GROUPS.datacenter, 'resource-center/overview', 'get'],
            },
            {
              title: 'computeResource',
              icon: 'icon-jiqunjiedian',
              children: [
                {
                  title: 'clusterNodes',
                  path: `${resource}/all/nodes`,
                  icon: IconBranch,
                  viewAuth: [GROUPS.node, 'nodes', 'list'],
                },
                {
                  title: 'computeAssets',
                  path: `${resource}/bare-metal/node`,
                  icon: BARE_METAL_NODE_ICON,
                  viewAuth: [GROUPS.computing, 'nodes', 'list'],
                },
                {
                  title: 'clusterAssets',
                  path: `${resource}/clusters/biz`,
                  icon: IconApps,
                  viewAuth: [GROUPS.clusterScheduler, 'clusters', 'list'],
                },
              ],
            },
            {
              title: 'schedulingResourcePool',
              icon: 'icon-jiqunjiedian',
              children: [
                {
                  title: 'controlPanel',
                  path: `${resource}/system/nodes`,
                  icon: IconCommand,
                  viewAuth: [GROUPS.node, 'nodes', 'list'],
                },
                {
                  title: 'networkPanel',
                  path: `${resource}/network/nodes`,
                  icon: IconWifi,
                  viewAuth: [GROUPS.node, 'nodes', 'list'],
                },
                {
                  title: 'generalContainer',
                  path: `${resource}/container-general/nodes`,
                  icon: IconApps,
                  viewAuth: [GROUPS.node, 'nodes', 'list'],
                },
                {
                  title: 'elasticCloudHost',
                  path: `${resource}/cloud-general/nodes`,
                  icon: IconCloud,
                  viewAuth: [GROUPS.node, 'nodes', 'list'],
                },
                {
                  title: 'elasticBareMetalNode',
                  path: `${resource}/cloud-hpc/nodes`,
                  icon: IconThunderbolt,
                  viewAuth: [GROUPS.node, 'nodes', 'list'],
                },
                {
                  title: 'bareMetalPool',
                  path: `${resource}/bare-metal/node-groups`,
                  icon: BARE_METAL_POOL_ICON,
                  viewAuth: [GROUPS.computing, 'node-groups', 'list'],
                },
              ],
            },
            {
              title: 'assetManagement',
              icon: 'icon-buchongjifei',
              children: [
                {
                  title: 'networkAssets',
                  path: `${resource}/bare-metal/gateway`,
                  icon: 'icon-gateway',
                  viewAuth: [GROUPS.computing, 'gateways', 'list'],
                },
                {
                  title: 'networkDevice',
                  path: `${resource}/network-device`,
                  icon: 'icon-jiaohuanji',
                  viewAuth: [GROUPS.network, 'networkdevices', 'list'],
                },
                {
                  title: 'storageAssets',
                  path: `${resource}/storageclasses`,
                  icon: 'icon-cunchulei',
                  // 与 auth-conf/assets/storageClasses、页面列表接口一致：storage.stack.io / storages / list
                  viewAuth: [GROUPS.storageStack, 'storages', 'list'],
                },
                {
                  title: 'hardwareAssets',
                  path: `${resource}/hardware`,
                  icon: 'icon-GPU',
                  viewAuth: [GROUPS.clusterScheduler, 'hardwares', 'list'],
                },
              ],
            },
          ],
        },
        {
          title: 'apiKeyManagement',
          icon: 'icon-api-key',
          path: `${dc}/model-center/api-key`,
          fixBottom: true,
          /** API Key 保持底部入口，并在 AI 与应用中心侧栏展示。 */
          onlyCenters: [SUB_CENTER.AI, SUB_CENTER.APP],
          viewAuth: [GROUPS.llmgw, 'llmgwapikeys', 'list'],
        },
        {
          title: 'dataCenterMembers',
          path: `/data-center/${cluster}/users`,
          icon: 'icon-pingtaiyonghu',
          fixBottom: true,
          viewAuth: [GROUPS.iam, 'users', 'list'],
        },
        {
          title: 'dataCenterRBAC',
          path: `/data-center/${cluster}/roles`,
          icon: 'icon-pingtaijiaose',
          fixBottom: true,
          viewAuth: [GROUPS.iam, 'roles', 'list'],
        },
        {
          title: 'audit' /**  审计菜单权限 */,
          path: `/data-center/${cluster}/audit`,
          icon: 'icon-Outline',
          fixBottom: true,
          viewAuth: [GROUPS.iam, 'audits', 'list'],
        },
      ],
    },
  ];
};
